import { createNextState } from '@reduxjs/toolkit';
import { useAppSelector } from 'dux/app/hooks';

import {
  haircareTopicalProducts,
  productsSlugs as ProductsSlugs,
  skincareProducts,
} from 'constants/products';
import { subscriptionCategories as SubscriptionCategories } from 'constants/subscriptions';

import { getFreeProductCatalog } from 'dux/membership/selectors';
import { getActiveOrOnHoldSubscriptionByCategory } from 'dux/subscriptions/selectors';
import { getHasAnsweredStylingGelQuestions } from 'dux/user/selectors';

import type { FreeProductSlug } from './slice';

/**
 * Returns the free product catalog filtered by existing subscriptions (active, onhold)
 */
export const useFreeProductCatalog = () => {
  const rawFreeProductCatalog = useAppSelector(getFreeProductCatalog);
  const hairSub = useAppSelector((state) =>
    getActiveOrOnHoldSubscriptionByCategory(state, {
      category: SubscriptionCategories.HAIRCARE,
    }),
  );
  const skinSub = useAppSelector((state) =>
    getActiveOrOnHoldSubscriptionByCategory(state, {
      category: SubscriptionCategories.SKINCARE,
    }),
  );
  const hasAnsweredStylingGelQuestions = useAppSelector(getHasAnsweredStylingGelQuestions);

  const hasHairSubscription = hairSub ?? false;
  const hasSkinSubscription = skinSub ?? false;

  const rejectedProductsSlugs = createNextState([] as Array<FreeProductSlug>, (draftArray) => {
    // if user has only haircare (topicals or supplements) subscription we reject the skincare products
    if (hasHairSubscription && !hasSkinSubscription) {
      draftArray.push(...skincareProducts);
    }
    // if user has only skincare subscription we reject the haircare products
    if (hasSkinSubscription && !hasHairSubscription) {
      draftArray.push(...haircareTopicalProducts);
    }
    // if user has not answered Styling Gel questions we reject Styling Gel
    if (!hasAnsweredStylingGelQuestions) {
      draftArray.push(ProductsSlugs.STYLING_GEL);
    }
  });

  const relevantFreeProducts = rawFreeProductCatalog.filter(
    ({ slug }) => !rejectedProductsSlugs.includes(slug),
  );

  return relevantFreeProducts;
};
